import React, { useState, useEffect, useRef, useMemo } from 'react';

type DateRangePreset = 'all' | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'this_year' | 'last_year' | 'custom';

interface DateRange {
  from: string | null;
  to: string | null;
}

interface DateRangeFilterProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

const formatDateISO = (date: Date): string => date.toISOString().split('T')[0];

const getPresetDateRange = (preset: DateRangePreset): DateRange => {
    const today = new Date();
    // Set time to noon to avoid timezone boundary issues with toISOString() which uses UTC
    today.setHours(12, 0, 0, 0);

    if (preset === 'all') {
        return { from: null, to: null };
    }

    let from: Date;
    let to: Date;

    switch (preset) {
        case 'this_week': {
            from = new Date(today);
            const day = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
            const diffToMonday = day === 0 ? 6 : day - 1; // days to subtract to get to Monday
            from.setDate(today.getDate() - diffToMonday);
            
            to = new Date(from);
            to.setDate(from.getDate() + 6);
            break;
        }
        case 'last_week': {
            from = new Date(today);
            const day = today.getDay();
            const diffToMonday = day === 0 ? 6 : day - 1;
            from.setDate(today.getDate() - diffToMonday - 7);
            
            to = new Date(from);
            to.setDate(from.getDate() + 6);
            break;
        }
        case 'this_month':
            from = new Date(today.getFullYear(), today.getMonth(), 1, 12, 0, 0, 0);
            to = new Date(today.getFullYear(), today.getMonth() + 1, 0, 12, 0, 0, 0);
            break;
        case 'last_month':
            from = new Date(today.getFullYear(), today.getMonth() - 1, 1, 12, 0, 0, 0);
            to = new Date(today.getFullYear(), today.getMonth(), 0, 12, 0, 0, 0);
            break;
        case 'this_year':
            from = new Date(today.getFullYear(), 0, 1, 12, 0, 0, 0);
            to = new Date(today.getFullYear(), 11, 31, 12, 0, 0, 0);
            break;
        case 'last_year':
            from = new Date(today.getFullYear() - 1, 0, 1, 12, 0, 0, 0);
            to = new Date(today.getFullYear() - 1, 11, 31, 12, 0, 0, 0);
            break;
        default:
            return { from: null, to: null };
    }
    return { from: formatDateISO(from), to: formatDateISO(to) };
};

const PRESET_LABELS: Record<DateRangePreset, string> = {
    all: 'Vše',
    this_week: 'Tento týden',
    last_week: 'Minulý týden',
    this_month: 'Tento měsíc',
    last_month: 'Minulý měsíc',
    this_year: 'Tento rok',
    last_year: 'Minulý rok',
    custom: 'Vlastní rozsah...',
};

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [customFrom, setCustomFrom] = useState(value.from || '');
    const [customTo, setCustomTo] = useState(value.to || '');
    const [activePreset, setActivePreset] = useState<DateRangePreset>('this_month');
    const wrapperRef = useRef<HTMLDivElement>(null);
    
    // Determine display label based on current value
    const displayLabel = useMemo(() => {
        if (!value.from && !value.to) return PRESET_LABELS.all;
        
        for (const [preset, label] of Object.entries(PRESET_LABELS)) {
            if (preset === 'custom') continue;
            const range = getPresetDateRange(preset as DateRangePreset);
            if (range.from === value.from && range.to === value.to) {
                return label;
            }
        }
        if(value.from || value.to) {
             const from = value.from ? new Date(value.from).toLocaleDateString('cs-CZ') : '...';
             const to = value.to ? new Date(value.to).toLocaleDateString('cs-CZ') : '...';
             return `${from} - ${to}`;
        }
        return PRESET_LABELS.all;

    }, [value]);
    
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [wrapperRef]);
    

    const handlePresetSelect = (preset: DateRangePreset) => {
        setActivePreset(preset);
        if (preset === 'custom') {
            setCustomFrom(value.from || '');
            setCustomTo(value.to || '');
        } else {
            onChange(getPresetDateRange(preset));
            setIsOpen(false);
        }
    };
    
    const handleCustomApply = () => {
        onChange({ from: customFrom || null, to: customTo || null });
        setIsOpen(false);
    }
    
    const presets: DateRangePreset[] = ['this_week', 'last_week', 'this_month', 'last_month', 'this_year', 'last_year', 'all', 'custom'];

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 border border-gray-300 rounded-md bg-white text-gray-700 w-48 text-left flex justify-between items-center"
            >
                <span className="text-sm">{displayLabel}</span>
                 <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </button>
            {isOpen && (
                <div className="absolute z-10 mt-1 w-64 bg-white rounded-md shadow-lg border">
                    {activePreset !== 'custom' ? (
                        <ul className="py-1">
                            {presets.map(preset => (
                                <li key={preset}>
                                    <button
                                        onClick={() => handlePresetSelect(preset)}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        {PRESET_LABELS[preset]}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="p-4 space-y-3">
                            <div>
                                <label className="text-xs font-medium text-gray-600 block mb-1">Datum od</label>
                                <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="p-1 border border-gray-300 rounded-md w-full text-sm" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-600 block mb-1">Datum do</label>
                                <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="p-1 border border-gray-300 rounded-md w-full text-sm" />
                            </div>
                            <div className='flex items-center justify-between pt-2'>
                                <button onClick={() => setActivePreset('this_month')} className="text-xs text-gray-500 hover:underline">Zpět</button>
                                <button onClick={handleCustomApply} className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">Použít</button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};